import { z } from "zod";
import ConfirmationModal from "../../../../ui/Modal/ConfirmationModal";
import Modal from "../../../../ui/Modal/Modal";
import { Controller, useForm } from "react-hook-form";
import TextInput from "../../../../ui/TextInput/TextInput";
import Label from "../../../../ui/Label/Label";
import styles from "./CreateNewTeamModal.module.scss";
import { graphql } from "../../../../__generated__/gql";
import { useMutation } from "urql";
import urls from "../../../../routes";
import { useToast } from "../../../../ui/Toast/ToastContext";
import useUser from "../../../../providers/UserProvider/useUser";
import { zodResolver } from "@hookform/resolvers/zod";

const CreateTeamMutation = graphql(/* GraphQL */ `
  mutation createTenant($name: String!) {
    createTenant(input: { name: $name }) {
      id
      identityId
      name
    }
  }
`);

const schema = z.object({
  name: z.string().min(1),
});

type CreateNewTeamFormData = z.infer<typeof schema>;

function InnerCreateNewTeamModal({ onClose }: { onClose: () => void }) {
  const [, createTeam] = useMutation(CreateTeamMutation);
  const {
    handleSubmit,
    control,
    formState: { isSubmitting, isSubmitSuccessful },
  } = useForm<CreateNewTeamFormData>({
    defaultValues: { name: "" },
    resolver: zodResolver(schema),
  });
  const { selectOrganization } = useUser();

  const { showToast } = useToast();

  const onSubmit = async (data: CreateNewTeamFormData) => {
    const createTeamResponse = await createTeam({ name: data.name });

    if (createTeamResponse.data) {
      await selectOrganization(createTeamResponse.data.createTenant.identityId);

      window.location.href = `${urls.home}?sign_in_with_org=${createTeamResponse.data?.createTenant.identityId}`;
    } else {
      showToast({ title: "Something went wrong", variant: "error" });
      onClose();
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
      <Label label="Team name">
        <Controller
          control={control}
          name="name"
          render={({ field }) => (
            <TextInput {...field} placeholder="Enter team name" />
          )}
        />
      </Label>
      <ConfirmationModal
        onClose={onClose}
        loading={isSubmitting || isSubmitSuccessful}
      />
    </form>
  );
}

function CreateNewTeamModal({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  return (
    <Modal title="Create new team" isOpen={isOpen} onClose={onClose}>
      <InnerCreateNewTeamModal onClose={onClose} />
    </Modal>
  );
}

export default CreateNewTeamModal;
